import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import {
  Brain, Cpu, Zap, Image, MessageSquare, BarChart3, Languages, Sparkles,
  Plus, Trash2, TestTube, Check, X, ChevronDown, ChevronUp, RefreshCw,
  Server, ArrowRight, Settings2, Activity, AlertCircle, Globe
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const TASK_ICONS = {
  order_agent: MessageSquare,
  chat: MessageSquare,
  marketing: Sparkles,
  analytics_narration: BarChart3,
  translation: Languages,
  description: Zap,
  image_generation: Image,
};

const SPEED_COLORS = {
  fast: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  medium: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  slow: 'text-red-400 bg-red-500/10 border-red-500/30',
};

const COST_COLORS = {
  low: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  medium: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  high: 'text-red-400 bg-red-500/10 border-red-500/30',
};

function ModelCard({ model, t, onTest, onDelete, testing }) {
  const isBuiltin = model.builtin;
  const caps = model.capabilities || [];

  return (
    <div
      data-testid={`model-card-${model.id}`}
      className={`relative rounded-xl border p-5 transition-all duration-200 hover:shadow-lg ${
        isBuiltin
          ? 'bg-gradient-to-br from-slate-800/80 to-slate-900/80 border-slate-700/50 hover:border-slate-600/60'
          : 'bg-gradient-to-br from-indigo-950/40 to-slate-900/80 border-indigo-500/20 hover:border-indigo-500/40'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
            caps.includes('image')
              ? 'bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30'
              : 'bg-gradient-to-br from-cyan-500/20 to-blue-500/20 border border-cyan-500/30'
          }`}>
            {caps.includes('image') ? <Image className="w-5 h-5 text-purple-400" /> : <Brain className="w-5 h-5 text-cyan-400" />}
          </div>
          <div>
            <h3 className="font-semibold text-white text-sm">{model.name}</h3>
            <p className="text-xs text-slate-500 font-mono">{model.id}</p>
          </div>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
          isBuiltin ? 'text-cyan-400 bg-cyan-500/10 border-cyan-500/30' : 'text-indigo-400 bg-indigo-500/10 border-indigo-500/30'
        }`}>
          {isBuiltin ? t('aiAdmin.builtIn') : t('aiAdmin.custom')}
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {caps.map(c => (
          <span key={c} className="text-[10px] px-2 py-0.5 rounded-full bg-slate-700/50 text-slate-300 border border-slate-600/30">
            {t(`aiAdmin.${c}`)}
          </span>
        ))}
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${SPEED_COLORS[model.speed] || SPEED_COLORS.medium}`}>
          {t(`aiAdmin.${model.speed}`)}
        </span>
        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${COST_COLORS[model.cost] || COST_COLORS.medium}`}>
          {t(`aiAdmin.cost`)}: {t(`aiAdmin.${model.cost}`)}
        </span>
      </div>

      {model.base_url && (
        <p className="text-[11px] text-slate-500 font-mono truncate mb-3">
          <Globe className="w-3 h-3 inline-block me-1" />{model.base_url}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          data-testid={`test-model-${model.id}`}
          disabled={testing === model.id}
          onClick={() => onTest(model.id)}
          className="text-xs bg-transparent border-slate-600/50 text-slate-300 hover:bg-slate-700/50 hover:text-white"
        >
          {testing === model.id ? <RefreshCw className="w-3.5 h-3.5 me-1 animate-spin" /> : <TestTube className="w-3.5 h-3.5 me-1" />}
          {testing === model.id ? t('aiAdmin.testing') : t('aiAdmin.test')}
        </Button>
        {!isBuiltin && (
          <Button
            size="sm"
            variant="outline"
            data-testid={`delete-model-${model.id}`}
            onClick={() => onDelete(model.id)}
            className="text-xs bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

function RoutingRow({ task, currentModel, models, t, onChange }) {
  const Icon = TASK_ICONS[task] || Cpu;
  return (
    <div data-testid={`routing-row-${task}`} className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/40 border border-slate-700/30 hover:border-slate-600/40 transition-all">
      <div className="w-9 h-9 rounded-lg bg-slate-700/50 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-slate-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-white">{t(`aiAdmin.tasks.${task}`)}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-500 shrink-0" />
      <select
        data-testid={`routing-select-${task}`}
        value={currentModel}
        onChange={(e) => onChange(task, e.target.value)}
        className="bg-slate-900/80 border border-slate-600/40 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-cyan-500/50 min-w-[180px]"
      >
        {models.map(m => (
          <option key={m.id} value={m.id}>{m.name}</option>
        ))}
      </select>
    </div>
  );
}

function AddModelForm({ t, onAdd, onCancel }) {
  const [form, setForm] = useState({ name: '', base_url: '', api_key: '', model_name: '', capabilities: ['text'], speed: 'medium', cost: 'low' });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.base_url) return;
    onAdd(form);
  };

  return (
    <form onSubmit={handleSubmit} data-testid="add-model-form" className="rounded-xl border border-indigo-500/30 bg-indigo-950/20 p-5 space-y-4">
      <h3 className="text-sm font-semibold text-indigo-300 flex items-center gap-2">
        <Server className="w-4 h-4" />
        {t('aiAdmin.addModel')}
      </h3>
      <p className="text-xs text-slate-400">{t('aiAdmin.selfHostedNote')}</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <input
          data-testid="add-model-name"
          placeholder={t('aiAdmin.modelName')}
          value={form.name}
          onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
          className="bg-slate-900/60 border border-slate-600/40 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
          required
        />
        <input
          data-testid="add-model-url"
          placeholder={`${t('aiAdmin.baseUrl')} (e.g. http://192.168.1.10:8080)`}
          value={form.base_url}
          onChange={e => setForm(p => ({ ...p, base_url: e.target.value }))}
          className="bg-slate-900/60 border border-slate-600/40 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
          required
        />
        <input
          data-testid="add-model-key"
          placeholder={`${t('aiAdmin.apiKey')} (${t('common.optional') || 'optional'})`}
          value={form.api_key}
          onChange={e => setForm(p => ({ ...p, api_key: e.target.value }))}
          className="bg-slate-900/60 border border-slate-600/40 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
          type="password"
        />
        <input
          data-testid="add-model-id"
          placeholder={`${t('aiAdmin.modelId')} (e.g. deepseek-v3)`}
          value={form.model_name}
          onChange={e => setForm(p => ({ ...p, model_name: e.target.value }))}
          className="bg-slate-900/60 border border-slate-600/40 rounded-lg px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-indigo-500/50"
        />
      </div>
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input type="checkbox" checked={form.capabilities.includes('text')} onChange={() => {}} className="rounded" disabled />
          {t('aiAdmin.text')}
        </label>
        <label className="flex items-center gap-2 text-xs text-slate-300">
          <input
            type="checkbox"
            checked={form.capabilities.includes('image')}
            onChange={e => setForm(p => ({ ...p, capabilities: e.target.checked ? [...p.capabilities, 'image'] : p.capabilities.filter(c => c !== 'image') }))}
            className="rounded"
          />
          {t('aiAdmin.image')}
        </label>
      </div>
      <div className="flex gap-2">
        <Button type="submit" size="sm" data-testid="add-model-submit" className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs">
          <Plus className="w-3.5 h-3.5 me-1" />{t('aiAdmin.addModel')}
        </Button>
        <Button type="button" size="sm" variant="outline" onClick={onCancel} className="text-xs bg-transparent border-slate-600/50 text-slate-400 hover:text-white">
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
}

export default function AdminAIModelsPage() {
  const { token } = useAdminAuth();
  const { t } = useTranslation();

  const [tab, setTab] = useState('models');
  const [models, setModels] = useState([]);
  const [routing, setRouting] = useState({});
  const [availableModels, setAvailableModels] = useState([]);
  const [usage, setUsage] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [modelsRes, routingRes, usageRes] = await Promise.all([
        axios.get(`${API}/api/ai/models`, { headers }),
        axios.get(`${API}/api/ai/routing`, { headers }),
        axios.get(`${API}/api/ai/usage`, { headers }),
      ]);
      setModels(modelsRes.data.models || []);
      setRouting(routingRes.data.mappings || {});
      setAvailableModels(routingRes.data.available_models || []);
      setUsage(usageRes.data);
    } catch (err) {
      console.error('Failed to fetch AI data:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleTest = async (modelId) => {
    setTesting(modelId);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/api/ai/test`, { model_id: modelId, prompt: 'قل مرحبا بجملة واحدة بالعربي.' }, { headers });
      setTestResult({ id: modelId, success: true, response: res.data.response || res.data.response_text || 'OK' });
    } catch (err) {
      setTestResult({ id: modelId, success: false, response: err.response?.data?.detail || err.message });
    } finally {
      setTesting(null);
    }
  };

  const handleAddModel = async (form) => {
    try {
      await axios.post(`${API}/api/ai/models`, form, { headers });
      setShowAddForm(false);
      fetchData();
    } catch (err) {
      console.error('Failed to add model:', err);
    }
  };

  const handleDeleteModel = async (modelId) => {
    try {
      await axios.delete(`${API}/api/ai/models/${modelId}`, { headers });
      fetchData();
    } catch (err) {
      console.error('Failed to delete model:', err);
    }
  };

  const handleRoutingChange = async (task, modelId) => {
    const updated = { ...routing, [task]: modelId };
    setRouting(updated);
    try {
      await axios.post(`${API}/api/ai/routing`, { mappings: updated }, { headers });
    } catch (err) {
      console.error('Failed to update routing:', err);
    }
  };

  const builtinModels = models.filter(m => m.builtin);
  const customModels = models.filter(m => !m.builtin);

  const TABS = [
    { id: 'models', icon: Brain, label: t('aiAdmin.models') },
    { id: 'routing', icon: Settings2, label: t('aiAdmin.routing') },
    { id: 'usage', icon: Activity, label: t('aiAdmin.usage') },
  ];

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-6 h-6 text-slate-400 animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto space-y-6" data-testid="ai-admin-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Brain className="w-6 h-6 text-cyan-400" />
              {t('aiAdmin.title')}
            </h1>
            <p className="text-sm text-slate-400 mt-1">{t('aiAdmin.subtitle')}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-800/40 rounded-xl p-1 border border-slate-700/30 w-fit">
          {TABS.map(tb => {
            const Icon = tb.icon;
            const active = tab === tb.id;
            return (
              <button
                key={tb.id}
                data-testid={`ai-tab-${tb.id}`}
                onClick={() => setTab(tb.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  active ? 'bg-cyan-500/15 text-cyan-400 border border-cyan-500/30' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/30'
                }`}
              >
                <Icon className="w-4 h-4" />{tb.label}
              </button>
            );
          })}
        </div>

        {/* Test Result Toast */}
        {testResult && (
          <div data-testid="test-result-banner" className={`flex items-center gap-3 p-3 rounded-lg border ${
            testResult.success
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
              : 'bg-red-500/10 border-red-500/30 text-red-300'
          }`}>
            {testResult.success ? <Check className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
            <span className="text-sm font-medium">{testResult.success ? t('aiAdmin.testSuccess') : t('aiAdmin.testFailed')}</span>
            <span className="text-xs text-slate-400 truncate flex-1">{testResult.response}</span>
            <button onClick={() => setTestResult(null)} className="text-slate-500 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Models Tab */}
        {tab === 'models' && (
          <div className="space-y-6">
            {/* Built-in Models */}
            <div>
              <h2 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
                <Cpu className="w-4 h-4 text-cyan-400" />
                {t('aiAdmin.builtIn')} ({builtinModels.length})
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {builtinModels.map(m => (
                  <ModelCard key={m.id} model={m} t={t} onTest={handleTest} onDelete={handleDeleteModel} testing={testing} />
                ))}
              </div>
            </div>

            {/* Custom Models */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                  <Server className="w-4 h-4 text-indigo-400" />
                  {t('aiAdmin.custom')} ({customModels.length})
                </h2>
                {!showAddForm && (
                  <Button
                    size="sm"
                    data-testid="show-add-model-btn"
                    onClick={() => setShowAddForm(true)}
                    className="bg-indigo-600/80 hover:bg-indigo-500 text-white text-xs"
                  >
                    <Plus className="w-3.5 h-3.5 me-1" />{t('aiAdmin.addModel')}
                  </Button>
                )}
              </div>

              {showAddForm && (
                <div className="mb-4">
                  <AddModelForm t={t} onAdd={handleAddModel} onCancel={() => setShowAddForm(false)} />
                </div>
              )}

              {customModels.length === 0 && !showAddForm && (
                <div className="text-center py-8 rounded-xl border border-dashed border-slate-700/50 bg-slate-800/20">
                  <Server className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">{t('aiAdmin.noCustomModels')}</p>
                  <p className="text-xs text-slate-600 mt-1">{t('aiAdmin.selfHostedNote')}</p>
                </div>
              )}

              {customModels.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {customModels.map(m => (
                    <ModelCard key={m.id} model={m} t={t} onTest={handleTest} onDelete={handleDeleteModel} testing={testing} />
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Routing Tab */}
        {tab === 'routing' && (
          <div className="space-y-4">
            <div className="rounded-xl border border-slate-700/30 bg-slate-800/20 p-5">
              <h2 className="text-sm font-semibold text-white mb-1 flex items-center gap-2">
                <Settings2 className="w-4 h-4 text-amber-400" />
                {t('aiAdmin.routing')}
              </h2>
              <p className="text-xs text-slate-500 mb-4">كل مهمة توجّه تلقائياً للنموذج المحدد. يمكنك تغيير النموذج لكل مهمة.</p>
              <div className="space-y-2">
                {Object.keys(routing).map(task => (
                  <RoutingRow
                    key={task}
                    task={task}
                    currentModel={routing[task]}
                    models={availableModels}
                    t={t}
                    onChange={handleRoutingChange}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Usage Tab */}
        {tab === 'usage' && usage && (
          <div className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: t('aiAdmin.totalCalls'), value: usage.total_calls, color: 'cyan' },
                { label: t('aiAdmin.todayCalls'), value: usage.today_calls, color: 'blue' },
                { label: t('aiAdmin.weekCalls'), value: usage.week_calls, color: 'purple' },
                { label: t('aiAdmin.successRate'), value: `${usage.success_rate}%`, color: usage.success_rate >= 95 ? 'emerald' : 'amber' },
              ].map((s, i) => (
                <div key={i} className={`rounded-xl border border-${s.color}-500/20 bg-${s.color}-500/5 p-4`}>
                  <p className="text-xs text-slate-400">{s.label}</p>
                  <p className={`text-2xl font-bold text-${s.color}-400 mt-1`}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* By Model */}
            {usage.by_model?.length > 0 && (
              <div className="rounded-xl border border-slate-700/30 bg-slate-800/20 p-5">
                <h3 className="text-sm font-semibold text-white mb-3">{t('aiAdmin.models')}</h3>
                <div className="space-y-2">
                  {usage.by_model.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-900/40 border border-slate-700/20">
                      <span className="text-sm text-slate-300 font-mono">{m.model_id}</span>
                      <div className="flex items-center gap-4">
                        <span className="text-sm text-cyan-400 font-medium">{m.count} calls</span>
                        {m.failures > 0 && <span className="text-xs text-red-400">{m.failures} failed</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* By Task */}
            {usage.by_task?.length > 0 && (
              <div className="rounded-xl border border-slate-700/30 bg-slate-800/20 p-5">
                <h3 className="text-sm font-semibold text-white mb-3">{t('aiAdmin.routing')}</h3>
                <div className="space-y-2">
                  {usage.by_task.map((tk, i) => {
                    const Icon = TASK_ICONS[tk.task] || Cpu;
                    return (
                      <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-slate-900/40 border border-slate-700/20">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-slate-400" />
                          <span className="text-sm text-slate-300">{t(`aiAdmin.tasks.${tk.task}`) || tk.task}</span>
                        </div>
                        <span className="text-sm text-cyan-400 font-medium">{tk.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {usage.total_calls === 0 && (
              <div className="text-center py-12 rounded-xl border border-dashed border-slate-700/50 bg-slate-800/20">
                <Activity className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                <p className="text-sm text-slate-500">لا توجد استدعاءات بعد</p>
              </div>
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
